import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Chapter } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API } from "@/lib/scroll";
import { useLang } from "@/i18n";

const Contact = () => {
  const { t, lang } = useLang();
  const c = t.contact;
  const TOPICS = c.topics;
  const [form, setForm] = useState({ name: "", email: "", topic: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const applyPreset = () => {
      const idx = sessionStorage.getItem("fb-topic-idx");
      if (idx !== null) {
        const i = parseInt(idx, 10);
        setForm((f) => ({ ...f, topic: TOPICS[i] || TOPICS[0] }));
        sessionStorage.removeItem("fb-topic-idx");
      }
    };
    applyPreset();
    window.addEventListener("fb-topic", applyPreset);
    return () => window.removeEventListener("fb-topic", applyPreset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        email: form.email,
        topic: form.topic || TOPICS[TOPICS.length - 1],
        message: form.message,
      });
      setDone(true);
      toast.success(c.toastOkTitle, { description: c.toastOkBody });
    } catch (err) {
      toast.error(c.toastErrTitle, { description: c.toastErrBody });
    } finally {
      setLoading(false);
    }
  };

  const field =
    "h-14 bg-neutral-900 border-white/10 rounded-none text-white placeholder:text-neutral-600 focus-visible:ring-[#D4AF37]";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#0A0A0A] border-t border-white/10"
    >
      <div className="container-x">
        <Chapter num="08" label={c.chapter} />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <Reveal className="lg:col-span-5">
            <h2 className="font-display text-4xl md:text-5xl lg:text-7xl uppercase tracking-tight leading-[0.9]">
              {c.titleA} <br />
              <span className="text-[#D91624]">{c.titleB}</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-neutral-400 leading-relaxed max-w-md">
              {c.intro}
            </p>
            <div className="mt-10 space-y-5">
              <div className="flex items-center gap-4">
                <span className="h-11 w-11 border border-[#D4AF37]/40 flex items-center justify-center">
                  <Mail size={18} className="text-[#D4AF37]" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-bold">{c.emailLabel}</p>
                  <p className="text-white font-semibold">hello@fitbersama.com</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="h-11 w-11 border border-[#D4AF37]/40 flex items-center justify-center">
                  <MessageSquare size={18} className="text-[#D4AF37]" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-bold">{c.waLabel}</p>
                  <p className="text-white font-semibold">{c.waValue}</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            {done ? (
              <div
                data-testid="contact-success"
                className="h-full border border-[#D4AF37]/40 bg-[#121212] p-10 flex flex-col justify-center"
              >
                <CheckCircle2 size={44} className="text-[#D4AF37]" />
                <h3 className="font-display text-4xl uppercase mt-5">{c.successTitle}</h3>
                <p className="mt-3 text-neutral-400 max-w-md">
                  {c.successThanks} {form.name.split(" ")[0]}. {c.successReply}{" "}
                  <span className="text-white font-semibold">{form.email}</span>{" "}
                  {c.successWithin}
                </p>
                <button
                  data-testid="contact-send-another-btn"
                  onClick={() => {
                    setDone(false);
                    setForm({ name: "", email: "", topic: "", message: "" });
                  }}
                  className="btn-outline mt-8 self-start"
                >
                  {c.sendAnother}
                </button>
              </div>
            ) : (
              <form
                data-testid="contact-form"
                onSubmit={submit}
                className="border border-white/10 bg-[#121212] p-8 md:p-10 space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
                      {c.name}
                    </label>
                    <Input
                      id="contact-name"
                      data-testid="contact-name-input"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder={c.namePlaceholder}
                      className={field}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
                      {c.email}
                    </label>
                    <Input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder={c.emailPlaceholder}
                      className={field}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
                    {c.interest}
                  </label>
                  <Select
                    value={form.topic}
                    onValueChange={(v) => setForm({ ...form, topic: v })}
                  >
                    <SelectTrigger
                      data-testid="contact-topic-select"
                      className="h-14 bg-neutral-900 border-white/10 rounded-none text-white focus:ring-[#D4AF37]"
                    >
                      <SelectValue placeholder={c.interestPlaceholder} />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-900 border-white/10 rounded-none">
                      {TOPICS.map((topic) => (
                        <SelectItem
                          key={topic}
                          value={topic}
                          data-testid={`contact-topic-${topic.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="text-white focus:bg-[#D91624] focus:text-white"
                        >
                          {topic}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
                    {c.goals}
                  </label>
                  <Textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder={c.goalsPlaceholder}
                    className="bg-neutral-900 border-white/10 rounded-none text-white placeholder:text-neutral-600 focus-visible:ring-[#D4AF37] resize-none"
                  />
                </div>
                <button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full disabled:opacity-50"
                >
                  {loading ? c.sending : c.submit} <ArrowRight size={16} />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
